import React from 'react';
import styled from 'styled-components';

const ChartContainer = styled.div`
  width: 100%;
  height: 300px;
  position: relative;
`;

const ChartSvg = styled.svg`
  width: 100%;
  height: 100%;
  overflow: visible;
`;

const ChartAxis = styled.line`
  stroke: var(--border-color);
  stroke-width: 1;
`;

const ChartGrid = styled.line`
  stroke: var(--border-color);
  stroke-width: 0.5;
  opacity: 0.3;
`;

const ChartLine = styled.path<{ color: string }>`
  fill: none;
  stroke: ${props => props.color};
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
`;

const ChartPoint = styled.circle<{ color: string }>`
  fill: ${props => props.color};
  stroke: white;
  stroke-width: 2;
  cursor: pointer;
  transition: r 0.2s ease;
  
  &:hover {
    r: 6;
  }
`;

const ChartLabel = styled.text`
  font-size: 12px;
  fill: var(--text-secondary);
  text-anchor: middle;
`;

const ChartYLabel = styled.text`
  font-size: 12px;
  fill: var(--text-secondary);
  text-anchor: end;
`;

const Legend = styled.div`
  display: flex;
  justify-content: center;
  gap: var(--spacing-lg);
  margin-top: var(--spacing-md);
  flex-wrap: wrap;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  font-size: 0.875rem;
`;

const LegendColor = styled.div<{ color: string }>`
  width: 16px;
  height: 3px;
  background-color: ${props => props.color};
  border-radius: 2px;
`;

const Tooltip = styled.div<{ x: number; y: number; visible: boolean }>`
  position: absolute;
  left: ${props => props.x}px;
  top: ${props => props.y}px;
  background: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  padding: var(--spacing-sm);
  font-size: 0.75rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  pointer-events: none;
  z-index: 10;
  opacity: ${props => props.visible ? 1 : 0};
  transition: opacity 0.2s ease;
  transform: translate(-50%, -100%);
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: var(--card-background);
  }
`;

interface DataPoint {
  x: number;
  y: number;
  label: string;
  date: string;
}

interface LineData {
  label: string;
  color: string;
  data: DataPoint[];
}

interface LineChartProps {
  data: LineData[];
  width?: number;
  height?: number;
  yMin?: number;
  yMax?: number;
}

export const LineChart: React.FC<LineChartProps> = ({
  data,
  width = 400,
  height = 300,
  yMin = 0,
  yMax = 10
}) => {
  const [tooltip, setTooltip] = React.useState<{
    visible: boolean;
    x: number;
    y: number;
    content: string;
  }>({ visible: false, x: 0, y: 0, content: '' });

  const margin = { top: 20, right: 20, bottom: 40, left: 40 };
  const chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;

  // Calculate scales
  const allDataPoints = data.flatMap(line => line.data);
  const xMin = Math.min(...allDataPoints.map(d => d.x));
  const xMax = Math.max(...allDataPoints.map(d => d.x));
  
  const xScale = (value: number) => ((value - xMin) / (xMax - xMin)) * chartWidth;
  const yScale = (value: number) => chartHeight - ((value - yMin) / (yMax - yMin)) * chartHeight;

  // Generate path for line
  const generatePath = (points: DataPoint[]) => {
    if (points.length === 0) return '';
    
    const sortedPoints = [...points].sort((a, b) => a.x - b.x);
    let path = `M ${xScale(sortedPoints[0].x)} ${yScale(sortedPoints[0].y)}`;
    
    for (let i = 1; i < sortedPoints.length; i++) {
      path += ` L ${xScale(sortedPoints[i].x)} ${yScale(sortedPoints[i].y)}`;
    }
    
    return path;
  };

  // Generate grid lines
  const yGridLines = [];
  const ySteps = 5;
  for (let i = 0; i <= ySteps; i++) {
    const y = (i / ySteps) * chartHeight;
    yGridLines.push(y);
  }

  const handlePointHover = (event: React.MouseEvent, point: DataPoint) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const containerRect = event.currentTarget.closest('div')?.getBoundingClientRect();
    
    if (containerRect) {
      setTooltip({
        visible: true,
        x: rect.left - containerRect.left + rect.width / 2,
        y: rect.top - containerRect.top,
        content: `${point.label}: ${point.y.toFixed(1)} (${point.date})`
      });
    }
  };

  const handlePointLeave = () => {
    setTooltip(prev => ({ ...prev, visible: false }));
  };

  if (data.length === 0 || allDataPoints.length === 0) {
    return (
      <ChartContainer>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100%',
          color: 'var(--text-secondary)',
          fontSize: '0.875rem'
        }}>
          No data available for chart
        </div>
      </ChartContainer>
    );
  }

  return (
    <ChartContainer>
      <ChartSvg viewBox={`0 0 ${width} ${height}`}>
        <g transform={`translate(${margin.left}, ${margin.top})`}>
          {/* Grid lines */}
          {yGridLines.map((y, i) => (
            <ChartGrid
              key={`grid-${i}`}
              x1={0}
              y1={y}
              x2={chartWidth}
              y2={y}
            />
          ))}
          
          {/* Axes */}
          <ChartAxis x1={0} y1={chartHeight} x2={chartWidth} y2={chartHeight} />
          <ChartAxis x1={0} y1={0} x2={0} y2={chartHeight} />
          
          {/* Y-axis labels */}
          {yGridLines.map((y, i) => {
            const value = yMax - (i / ySteps) * (yMax - yMin);
            return (
              <ChartYLabel key={`y-label-${i}`} x={-10} y={y + 4}>
                {value.toFixed(1)}
              </ChartYLabel>
            );
          })}
          
          {/* Lines */}
          {data.map((line, lineIndex) => (
            <g key={`line-${lineIndex}`}>
              <ChartLine
                color={line.color}
                d={generatePath(line.data)}
              />
              {/* Points */}
              {line.data.map((point, pointIndex) => (
                <ChartPoint
                  key={`point-${lineIndex}-${pointIndex}`}
                  color={line.color}
                  cx={xScale(point.x)}
                  cy={yScale(point.y)}
                  r={4}
                  onMouseEnter={(e) => handlePointHover(e, point)}
                  onMouseLeave={handlePointLeave}
                />
              ))}
            </g>
          ))}
          
          {/* X-axis labels */}
          {allDataPoints
            .filter((point, index, arr) => 
              arr.findIndex(p => p.x === point.x) === index
            )
            .sort((a, b) => a.x - b.x)
            .map((point, i) => (
              <ChartLabel
                key={`x-label-${i}`}
                x={xScale(point.x)}
                y={chartHeight + 20}
              >
                {point.date}
              </ChartLabel>
            ))}
        </g>
      </ChartSvg>
      
      <Legend>
        {data.map((line, index) => (
          <LegendItem key={`legend-${index}`}>
            <LegendColor color={line.color} />
            <span>{line.label}</span>
          </LegendItem>
        ))}
      </Legend>
      
      <Tooltip
        x={tooltip.x}
        y={tooltip.y}
        visible={tooltip.visible}
      >
        {tooltip.content}
      </Tooltip>
    </ChartContainer>
  );
};